'use client';

import { useEffect, useState } from 'react';
import { Link } from '@/i18n/routing';
import { getBikeparks } from '@/lib/api';
import { Bikepark } from '@/types';
import { useTranslations, useLocale } from 'next-intl';

export default function PriceComparisonPage() {
  const t = useTranslations('priceComparison');
  const tCommon = useTranslations('common');
  const locale = useLocale();
  const [bikeparks, setBikeparks] = useState<Bikepark[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'price' | 'trails'>('trails');

  useEffect(() => {
    async function loadBikeparks() {
      const data = await getBikeparks(locale);
      setBikeparks(data);
      setLoading(false);
    }
    loadBikeparks();
  }, [locale]);

  const getSortedParks = () => {
    const filtered = bikeparks.filter(p => p.price !== undefined && p.price !== null);

    switch (sortBy) {
      case 'price':
        return [...filtered].sort((a, b) => {
          if (a.price === 0 && b.price > 0) return -1;
          if (b.price === 0 && a.price > 0) return 1;
          return (a.price || 0) - (b.price || 0);
        });
      case 'trails':
        return [...filtered].sort((a, b) => {
          const aCount = a.trailsCount || a.trails?.length || 0;
          const bCount = b.trailsCount || b.trails?.length || 0;
          return bCount - aCount;
        });
      default:
        return filtered;
    }
  };

  const sortedParks = getSortedParks();
  const avgPrice = bikeparks.filter(p => p.price && p.price > 0).reduce((sum, p) => sum + (p.price || 0), 0) / bikeparks.filter(p => p.price && p.price > 0).length;
  const freeParks = bikeparks.filter(p => p.price === 0);

  if (loading) {
    return (
      <div className="price-page">
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h2>{tCommon('loading')}...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="price-page">
      <header className="price-header">
        <div className="price-header-content">
          <h1>💰 {t('titleFull')}</h1>
          <p className="header-lead">
            {t('subtitle')}
          </p>

          <div className="price-stats">
            <div className="stat-card">
              <div className="stat-value">{Math.round(avgPrice)} zł</div>
              <div className="stat-label">{t('avgPrice')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{freeParks.length}</div>
              <div className="stat-label">{t('freeParks')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{bikeparks.length}</div>
              <div className="stat-label">{t('allLocations')}</div>
            </div>
          </div>

          <div className="price-nav">
            <Link href="/bikeparki" className="btn-secondary">🗺️ {tCommon('map')}</Link>
            <Link href="/bikeparki/lista" className="btn-secondary">📋 {tCommon('list')}</Link>
            <Link href="/bikeparki/ranking" className="btn-secondary">🏆 {tCommon('ranking')}</Link>
          </div>
        </div>
      </header>

      <div className="price-container">
        <section className="price-intro">
          <h2>{t('factorsTitle')}</h2>
          <div className="intro-grid">
            <div className="intro-item">
              <div className="intro-icon">🏔️</div>
              <h3>{t('factorLocationTitle')}</h3>
              <p>{t('factorLocationText')}</p>
            </div>
            <div className="intro-item">
              <div className="intro-icon">🛤️</div>
              <h3>{t('factorTrailsTitle')}</h3>
              <p>{t('factorTrailsText')}</p>
            </div>
            <div className="intro-item">
              <div className="intro-icon">⚙️</div>
              <h3>{t('factorInfrastructureTitle')}</h3>
              <p>{t('factorInfrastructureText')}</p>
            </div>
            <div className="intro-item">
              <div className="intro-icon">📅</div>
              <h3>{t('factorSeasonTitle')}</h3>
              <p>{t('factorSeasonText')}</p>
            </div>
          </div>
        </section>

        <section className="sorting-section">
          <h2>💳 {t('tableTitle')}</h2>
          <div className="sort-buttons">
            <button
              className={`sort-btn ${sortBy === 'price' ? 'active' : ''}`}
              onClick={() => setSortBy('price')}
            >
              {t('sortByPrice')}
            </button>
            <button
              className={`sort-btn ${sortBy === 'trails' ? 'active' : ''}`}
              onClick={() => setSortBy('trails')}
            >
              {t('sortByTrails')}
            </button>
          </div>
        </section>

        <div className="price-table-container">
          <table className="price-table">
            <thead>
              <tr>
                <th>{t('tableNo')}</th>
                <th>{t('tableBikepark')}</th>
                <th>{t('tableRegion')}</th>
                <th>{t('tableDayPass')}</th>
                <th>{t('tableTrailsCount')}</th>
                <th>{t('tableRating')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedParks.map((park, index) => (
                <tr key={park.documentId}>
                  <td className="rank-col">{index + 1}</td>
                  <td className="park-name">
                    <strong>{park.name}</strong>
                  </td>
                  <td>{park.region}</td>
                  <td className="price-cell">
                    {park.price === 0 ? (
                      <span className="price-free">💚 {t('free')}</span>
                    ) : (
                      <span className="price-paid">{park.price} zł</span>
                    )}
                  </td>
                  <td className="trails-col">{park.trailsCount || park.trails?.length || '-'}</td>
                  <td className="rating-cell">
                    {'★'.repeat(park.rating)}{'☆'.repeat(5 - park.rating)}
                  </td>
                  <td>
                    <Link href={`/bikepark/${park.slug}`} className="table-link">
                      {t('view')} →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="price-tips">
          <h2>{t('savingsTipsTitle')}</h2>
          <div className="tips-grid">
            <div className="tip-card">
              <div className="tip-number">1</div>
              <h3>{t('tip1Title')}</h3>
              <p>{t('tip1Text')}</p>
            </div>
            <div className="tip-card">
              <div className="tip-number">2</div>
              <h3>{t('tip2Title')}</h3>
              <p>{t('tip2Text')}</p>
            </div>
            <div className="tip-card">
              <div className="tip-number">3</div>
              <h3>{t('tip3Title')}</h3>
              <p>{t('tip3Text')}</p>
            </div>
            <div className="tip-card">
              <div className="tip-number">4</div>
              <h3>{t('tip4Title')}</h3>
              <p>{t('tip4Text')}</p>
            </div>
          </div>
        </section>

        <div className="price-note">
          <p>
            <strong>📌 {t('noteTitle')}:</strong> {t('noteDescription', {
              date: new Date().toLocaleDateString(locale === 'pl' ? 'pl-PL' : 'en-US', { month: 'long', year: 'numeric' })
            })}
          </p>
        </div>
      </div>

      <style jsx>{`
        .price-page {
          min-height: 100vh;
          background: var(--bg);
          color: var(--text);
        }

        .price-header {
          background: linear-gradient(135deg, var(--panel), var(--bg));
          padding: 40px 20px;
          border-bottom: 1px solid var(--border);
        }

        .price-header-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .price-header h1 {
          font-size: clamp(28px, 5vw, 40px);
          margin: 0 0 16px;
        }

        .header-lead {
          font-size: 16px;
          color: var(--text-secondary);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .price-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          text-align: center;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: var(--accent);
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: var(--muted);
        }

        .price-nav {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-secondary {
          display: inline-block;
          padding: 8px 16px;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          background: var(--panel);
          border: 1px solid var(--border);
          color: var(--text);
        }

        .btn-secondary:hover {
          background: var(--accent);
          color: #fff;
        }

        .price-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .price-intro {
          margin-bottom: 48px;
        }

        .price-intro h2 {
          font-size: 28px;
          margin: 0 0 24px;
        }

        .intro-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
        }

        .intro-item {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          transition: all 0.3s;
        }

        .intro-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          border-color: var(--accent);
        }

        .intro-icon {
          font-size: 40px;
          margin-bottom: 12px;
        }

        .intro-item h3 {
          font-size: 18px;
          margin: 0 0 8px;
          color: var(--text);
        }

        .intro-item p {
          font-size: 14px;
          line-height: 1.6;
          color: var(--muted);
          margin: 0;
        }

        .sorting-section {
          margin-bottom: 32px;
        }

        .sorting-section h2 {
          font-size: 28px;
          margin: 0 0 20px;
        }

        .sort-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .sort-btn {
          padding: 10px 18px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--panel);
          color: var(--text);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .sort-btn:hover {
          background: var(--accent);
          border-color: var(--accent);
          color: #fff;
        }

        .sort-btn.active {
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-color: transparent;
          color: #fff;
        }

        .price-table-container {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid var(--border);
          background: var(--panel);
          margin-bottom: 48px;
        }

        .price-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .price-table thead {
          background: var(--accent);
          color: #fff;
        }

        .price-table th {
          padding: 14px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .price-table tbody tr {
          border-bottom: 1px solid var(--border);
          transition: background 0.2s;
        }

        .price-table tbody tr:hover {
          background: var(--bg);
        }

        .price-table tbody tr:last-child {
          border-bottom: none;
        }

        .price-table td {
          padding: 14px 16px;
          color: var(--text);
        }

        .rank-col {
          font-weight: 600;
          color: var(--muted);
        }

        .park-name {
          font-size: 15px;
        }

        .price-cell {
          font-weight: 600;
        }

        .price-free {
          color: #10b981;
          font-weight: 700;
        }

        .price-paid {
          color: var(--text);
        }

        .trails-col {
          text-align: center;
        }

        .rating-cell {
          color: #ffd24a;
          font-size: 15px;
        }

        .value-col {
          text-align: center;
          font-weight: 600;
        }

        .value-excellent {
          color: #10b981;
          font-weight: 700;
        }

        .value-good {
          color: #3b82f6;
        }

        .value-ok {
          color: var(--text);
        }

        .table-link {
          color: var(--accent);
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s;
          display: inline-block;
        }

        .table-link:hover {
          color: var(--primary-hover);
          transform: translateX(2px);
        }

        .price-tips {
          margin-bottom: 40px;
        }

        .price-tips h2 {
          font-size: 28px;
          margin: 0 0 24px;
        }

        .tips-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 20px;
        }

        .tip-card {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          transition: all 0.3s;
        }

        .tip-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          border-color: var(--accent);
        }

        .tip-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .tip-card h3 {
          font-size: 18px;
          margin: 0 0 8px;
          color: var(--text);
        }

        .tip-card p {
          font-size: 14px;
          line-height: 1.6;
          color: var(--muted);
          margin: 0;
        }

        .price-note {
          margin-top: 32px;
          padding: 20px;
          background: rgba(59, 130, 246, 0.1);
          border-left: 4px solid var(--accent);
          border-radius: 8px;
        }

        .price-note p {
          margin: 0;
          font-size: 14px;
          line-height: 1.7;
          color: var(--text-secondary);
        }

        @media (max-width: 768px) {
          .price-header {
            padding: 24px 16px;
          }

          .price-container {
            padding: 24px 16px;
          }

          .price-stats {
            grid-template-columns: 1fr;
          }

          .intro-grid,
          .tips-grid {
            grid-template-columns: 1fr;
          }

          .price-table {
            font-size: 12px;
          }

          .price-table th,
          .price-table td {
            padding: 10px 8px;
          }

          .rank-col {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
